import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { controlToastState } from "risa-oasis-ui_v2";
import { RootState } from "../../../../../redux/store/store";
import {
  BiosimilarConfigDoc,
  BiosimilarConfigSavedMeta,
  BiosimilarFamily,
} from "../types";
import {
  fetchBiosimilarConfig,
  saveBiosimilarConfig,
} from "../utils/biosimilarConfigService";
import {
  configSignature,
  filterFamilies,
  hasFamilyName,
} from "../utils/biosimilarConfigHelpers";

/**
 * Result of the biosimilar config editor hook.
 */
export interface UseBiosimilarConfigEditorResult {
  loading: boolean;
  saving: boolean;
  error: Error | null;
  config: BiosimilarConfigDoc | null;
  filterQuery: string;
  setFilterQuery: (value: string) => void;
  filteredFamilies: BiosimilarFamily[];
  filteredFamilyIndexes: number[];
  isDirty: boolean;
  /** Audit fields from the last saved document, for the footer. */
  lastSavedMeta: BiosimilarConfigSavedMeta | null;
  handleEnabledChange: (enabled: boolean) => void;
  updateFamily: (index: number, patch: Partial<BiosimilarFamily>) => void;
  deleteFamily: (index: number) => void;
  /** True when a family with this label already exists. */
  hasFamilyName: (name: string) => boolean;
  /** Appends a family and writes the full document to Firestore. */
  addFamily: (family: BiosimilarFamily) => Promise<void>;
  saveChanges: () => Promise<void>;
  discardChanges: () => void;
}

const EMPTY_CONFIG: BiosimilarConfigDoc = {
  enabled: true,
  version: 1,
  families: [],
  org_ids: [],
  denial_outcomes: [],
};

/**
 * Loads, edits, and saves the biosimilar families Firestore document.
 *
 * @returns Editor state and mutation handlers for the config page
 */
export const useBiosimilarConfigEditor = (): UseBiosimilarConfigEditorResult => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [baseline, setBaseline] = useState<BiosimilarConfigDoc | null>(null);
  const [config, setConfig] = useState<BiosimilarConfigDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filterQuery, setFilterQuery] = useState("");

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchBiosimilarConfig();
      const nextConfig = result ?? { ...EMPTY_CONFIG };
      setConfig(nextConfig);
      setBaseline(nextConfig);
    } catch (err) {
      setConfig(null);
      setBaseline(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const isDirty = useMemo(() => {
    if (!config || !baseline) {
      return false;
    }
    return configSignature(config) !== configSignature(baseline);
  }, [config, baseline]);

  const filteredFamilies = useMemo(() => {
    const families = config?.families ?? [];
    return filterFamilies(families, filterQuery);
  }, [config?.families, filterQuery]);

  const filteredFamilyIndexes = useMemo(() => {
    const families = config?.families ?? [];
    const normalized = filterQuery.trim().toLowerCase();
    if (!normalized) {
      return families.map((_, index) => index);
    }
    return families
      .map((family, index) =>
        (family.family ?? "").toLowerCase().includes(normalized) ? index : -1,
      )
      .filter((index) => index >= 0);
  }, [config?.families, filterQuery]);

  const handleEnabledChange = useCallback((enabled: boolean) => {
    setConfig((prev) => (prev ? { ...prev, enabled } : prev));
  }, []);

  const updateFamily = useCallback(
    (index: number, patch: Partial<BiosimilarFamily>) => {
      setConfig((prev) => {
        if (!prev?.families) {
          return prev;
        }
        const families = [...prev.families];
        families[index] = { ...families[index], ...patch };
        return { ...prev, families };
      });
    },
    [],
  );

  const deleteFamily = useCallback((index: number) => {
    setConfig((prev) => {
      if (!prev?.families) {
        return prev;
      }
      return {
        ...prev,
        families: prev.families.filter((_, familyIndex) => familyIndex !== index),
      };
    });
  }, []);

  const familyNameExists = useCallback(
    (name: string) => hasFamilyName(config?.families ?? [], name),
    [config?.families],
  );

  const addFamily = useCallback(
    async (family: BiosimilarFamily) => {
      const base = config ?? { ...EMPTY_CONFIG };
      const nextConfig: BiosimilarConfigDoc = {
        ...base,
        families: [...(base.families ?? []), family],
      };
      setSaving(true);
      try {
        await saveBiosimilarConfig(nextConfig, user?.email || "N/A");
        const refreshed = await fetchBiosimilarConfig();
        const saved = refreshed ?? nextConfig;
        setConfig(saved);
        setBaseline(saved);
        controlToastState("biosimilar-config-family-add-success");
      } catch (err) {
        console.error("[biosimilarConfig] add family failed", err);
        controlToastState("biosimilar-config-family-add-failure");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [config, user?.email],
  );

  const discardChanges = useCallback(() => {
    if (baseline) {
      setConfig(baseline);
    }
  }, [baseline]);

  const lastSavedMeta = useMemo<BiosimilarConfigSavedMeta | null>(() => {
    if (!baseline) {
      return null;
    }
    return {
      updated_at: baseline.updated_at,
      updated_by: baseline.updated_by,
    };
  }, [baseline]);

  const saveChanges = useCallback(async () => {
    if (!config) {
      return;
    }
    setSaving(true);
    try {
      await saveBiosimilarConfig(config, user?.email || "N/A");
      const refreshed = await fetchBiosimilarConfig();
      const nextConfig = refreshed ?? config;
      setConfig(nextConfig);
      setBaseline(nextConfig);
      controlToastState("biosimilar-config-save-success");
    } catch (err) {
      console.error("[biosimilarConfig] save failed", err, user?.email);
      controlToastState("biosimilar-config-save-failure");
    } finally {
      setSaving(false);
    }
  }, [config, user?.email]);

  return {
    loading,
    saving,
    error,
    config,
    filterQuery,
    setFilterQuery,
    filteredFamilies,
    filteredFamilyIndexes,
    isDirty,
    lastSavedMeta,
    handleEnabledChange,
    updateFamily,
    deleteFamily,
    hasFamilyName: familyNameExists,
    addFamily,
    saveChanges,
    discardChanges,
  };
};
